import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSchemaError } from '../lib/supabase';


const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        console.warn('Session error:', error?.message);
        // Clear invalid session
        if (error?.message?.includes('Refresh Token') || error?.message?.includes('refresh_token')) {
          supabase?.auth?.signOut();
        }
      }
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      if (_event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
      } else if (_event === 'SIGNED_OUT') {
        setUser(null);
      } else {
        setUser(session?.user ?? null);
      }
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign up:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign in:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });
      if (error) throw error;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const updateUserMetadata = async (updates) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        data: updates
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update user metadata error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
